import { Injectable } from "@angular/core";
import jsPDF from "jspdf";
import html2canvas from 'html2canvas';
import { TimeTrack } from "src/app/entities/time-track/time-track";
import { Project } from "src/app/entities/project/project";
import { User } from "src/app/entities/user/user";


/** Data required to generate a time tracking report */
interface ReportData {
    projects: Project[];
    timeTracks: TimeTrack[];
    users: User[];
    dateRange: {
        start: Date;
        end: Date;
    };
    filters: {
        projects?: string[];
        clients?: string[];
        tags?: string[];
        users?: string[];
    };
}


/** Basic summary item with name, hours and color */
interface SummaryItem {
    name: string;
    hours: number;
    color: string;
}


/** Detailed summary including track title */
interface DetailedSummaryItem extends SummaryItem {
    title: string;
}


/** RGB color components */
interface RGB {
    r: number;
    g: number;
    b: number;
}


/** Chart dimensions for PDF export */
interface ChartDimensions {
    width: number;
    height: number;
    x: number;
    y: number;
}


/** PDF generation options */
interface PdfOptions {
    pageWidth: number;
    margins: {
        left: number;
        right: number;
        top: number;
        bottom: number;
    };
    spacing: {
        paragraph: number;
        section: number;
    };
    colors: {
        primary: string;
        secondary: string;
        background: string;
        accent: string;
        success: string;
        border: string;
    };
    typography: {
        headerFont: string;
        bodyFont: string;
        baseSize: number;
        titleSize: number;
        subtitleSize: number;
        headerSize: number;
        smallSize: number;
    };
}


/**
 * Service used to export data to other generated file types.
 *
 * Current file types supported:
 *  - `PDF`
 *  - `CSV`
 *
 * @TODO: Add exportation to EXCEL.
 */
@Injectable({
    providedIn: 'root'
})
export class ExportationService {


    /** Enhanced PDF generation options */
    private readonly pdfOptions: PdfOptions = {
        pageWidth: 0, // Will be set dynamically
        margins: {
            left: 25,
            right: 25,
            top: 20,
            bottom: 20
        },
        spacing: {
            paragraph: 10,
            section: 30
        },
        colors: {
            primary: '#2c3e50',    // Dark blue/slate for headings
            secondary: '#7f8c8d',  // Muted gray for secondary text
            background: '#f8f9fa', // Light gray for backgrounds
            accent: '#3498db',     // Bright blue for accents and highlights
            success: '#2ecc71',    // Green for positive values
            border: '#e0e0e0'      // Light gray for borders and dividers
        },
        typography: {
            headerFont: 'helvetica',
            bodyFont: 'helvetica',
            baseSize: 10,
            titleSize: 24,
            subtitleSize: 16,
            headerSize: 14,
            smallSize: 8
        }
    };


    /**
     * Converts seconds to hours with 1 decimal precision
     */
    private secondsToHours(seconds: number): number {
        return Math.round((seconds / 3600) * 10) / 10;
    }

    /**
     * Formats seconds to hours, minutes and seconds (HH:MM:SS)
     */
    private formatTimeHMS(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        return `${hours}h ${minutes}m ${secs}s`;
    }


    /**
     * Gets aggregated time data by project
     */
    private getSummaryData(data: ReportData): SummaryItem[] {
        const projectMap = new Map<string, SummaryItem>();

        data.timeTracks.forEach(track => {
            if (track.project) {
                const current = projectMap.get(track.project.name);
                projectMap.set(track.project.name, {
                    name: track.project.name,
                    hours: (current?.hours || 0) + this.secondsToHours(track.time_spent || 0),
                    color: track.project.color
                });
            }
        });

        return Array.from(projectMap.values())
            .sort((a, b) => b.hours - a.hours);
    }


    /**
     * Gets detailed time data per track
     */
    private getDetailedSummaryData(data: ReportData): DetailedSummaryItem[] {
        return data.timeTracks
            .filter(track => track.project && track.time_spent)
            .map(track => ({
                name: track.project!.name,
                title: track.title,
                hours: this.secondsToHours(track.time_spent!),
                color: track.project!.color
            }))
            .sort((a, b) => b.hours - a.hours);
    }


    /**
     * Converts hex color to RGB components
     */
    private hexToRgb(hex: string): RGB {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
    }


    /**
     * Adds a styled section header to the PDF
     */
    private addSectionHeader(doc: jsPDF, text: string, yOffset: number): number {
        // Section header with improved typography
        doc.setFontSize(this.pdfOptions.typography.headerSize);
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setTextColor(this.pdfOptions.colors.primary);
        doc.text(text, this.pdfOptions.margins.left, yOffset);

        // Add gradient accent line under header
        const accentColor = this.hexToRgb(this.pdfOptions.colors.accent);
        doc.setFillColor(accentColor.r, accentColor.g, accentColor.b);

        // Create an accent bar with better proportions
        const lineWidth = Math.min(100, doc.getStringUnitWidth(text) * this.pdfOptions.typography.headerSize * 0.4);
        doc.roundedRect(
            this.pdfOptions.margins.left,
            yOffset + 2,
            lineWidth,
            3,
            1,
            1,
            'F'
        );

        return yOffset + this.pdfOptions.spacing.section;
    }


    /**
     * Adds a colored item with improved styling to the PDF
     */
    private addColoredItem(doc: jsPDF, text: string, color: string, x: number, y: number): number {
        const rgb = this.hexToRgb(color);

        // Draw a rounded colored indicator
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.roundedRect(x, y - 5, 4, 4, 1, 1, 'F');

        // Add subtle shadow effect for depth
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.roundedRect(x, y - 5, 4, 4, 1, 1, 'S');

        // Set clear typography
        doc.setFontSize(this.pdfOptions.typography.baseSize);
        doc.setFont(this.pdfOptions.typography.bodyFont, 'normal');
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        // Add text with padding
        doc.text(text, x + 7, y);

        return y + this.pdfOptions.spacing.paragraph;
    }


    /**
     * Adds a chart image to the PDF with enhanced quality and visual styling
     */
    private async addChartImage(
        doc: jsPDF,
        element: HTMLElement,
        dimensions: ChartDimensions,
        yOffset: number
    ): Promise<number> {
        // For bar charts, adjust the element height before capturing
        const isBarChart = element.classList.contains('demo-chart') &&
            !element.classList.contains('summary__donut-chart') &&
            !element.classList.contains('donut-chart');

        // Store original styles to restore later
        const originalHeight = element.style.height;
        const originalWidth = element.style.width;
        const originalPosition = element.style.position;
        const originalDisplay = element.style.display;
        const originalVisibility = element.style.visibility;

        // Store SVG elements and their styles outside the if block for proper scope
        const svgElements = element.querySelectorAll('svg');
        const originalSvgStyles: { width: string; height: string; viewBox: string | null }[] = [];

        // Store original SVG styles regardless of chart type
        Array.from(svgElements).forEach(svg => {
            originalSvgStyles.push({
                width: svg.style.width,
                height: svg.style.height,
                viewBox: svg.getAttribute('viewBox')
            });
        });

        // Make sure the element is visible for rendering
        element.style.visibility = 'visible';
        element.style.display = 'block';

        // Temporarily modify element for all chart types to ensure proper sizing
        // Use larger dimensions for better quality
        element.style.position = 'relative';
        element.style.width = '800px'; // Force a large width for better quality
        element.style.height = isBarChart ? '600px' : '600px'; // Adjust height based on chart type

        // Apply new styles to SVG elements and their containers
        Array.from(svgElements).forEach(svg => {
            svg.style.width = '100%';
            svg.style.height = '100%';

            // Set viewBox to ensure content fills the SVG
            if (!svg.getAttribute('viewBox')) {
                const width = svg.getAttribute('width') || svg.clientWidth;
                const height = svg.getAttribute('height') || svg.clientHeight;
                svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            }

            // Find and modify any chart container divs
            const parentDiv = svg.parentElement;
            if (parentDiv && parentDiv.style) {
                parentDiv.style.width = '100%';
                parentDiv.style.height = '100%';
            }
        });

        // Find any canvas elements that might be used for charts
        const canvasElements = element.querySelectorAll('canvas');
        Array.from(canvasElements).forEach(canvas => {
            canvas.style.width = '100%';
            canvas.style.height = '100%';
        });

        // Force a reflow to ensure the chart is properly rendered
        element.getBoundingClientRect();

        // Longer delay to ensure chart is fully rendered with new dimensions
        await new Promise(resolve => setTimeout(resolve, 300));

        // Apply additional styling to ensure chart fills the container
        const chartContainers = element.querySelectorAll('.echarts-for-react, .echarts, .echarts-container, [_echarts_instance_]');
        Array.from(chartContainers).forEach(container => {
            if (container instanceof HTMLElement) {
                container.style.width = '100%';
                container.style.height = '100%';
            }
        });

        // Force another reflow after adjusting containers
        element.getBoundingClientRect();

        // Capture chart with higher resolution and quality settings
        const canvas = await html2canvas(element, {
            scale: 4, // Higher resolution for sharper charts
            width: parseInt(element.style.width),
            height: parseInt(element.style.height),
            backgroundColor: null,
            logging: false,
            useCORS: true, // Enable cross-origin image loading if needed
            allowTaint: true,
            onclone: (documentClone, elementClone) => {
                // Additional modifications to the cloned element before rendering
                // Ensure the clone has the same dimensions
                elementClone.style.width = element.style.width;
                elementClone.style.height = element.style.height;
                elementClone.style.visibility = 'visible';
                elementClone.style.display = 'block';

                // Find and modify any SVG elements in the clone
                const clonedSvgs = elementClone.querySelectorAll('svg');
                Array.from(clonedSvgs).forEach(svg => {
                    svg.style.width = '100%';
                    svg.style.height = '100%';
                    svg.setAttribute('preserveAspectRatio', 'none');
                });

                // Also modify any chart containers in the clone
                const chartContainers = elementClone.querySelectorAll('.echarts-for-react, .echarts, .echarts-container, [_echarts_instance_]');
                Array.from(chartContainers).forEach(container => {
                    if (container instanceof HTMLElement) {
                        container.style.width = '100%';
                        container.style.height = '100%';
                    }
                });

                // Find any canvas elements that might be used for charts
                const canvasElements = elementClone.querySelectorAll('canvas');
                Array.from(canvasElements).forEach(canvas => {
                    canvas.style.width = '100%';
                    canvas.style.height = '100%';
                });
            }
        });

        // Restore original styles
        element.style.height = originalHeight;
        element.style.width = originalWidth;
        element.style.position = originalPosition;
        element.style.display = originalDisplay;
        element.style.visibility = originalVisibility;

        // Restore SVG styles if we modified them
        Array.from(svgElements).forEach((svg, i) => {
            if (i < originalSvgStyles.length) {
                svg.style.width = originalSvgStyles[i].width;
                svg.style.height = originalSvgStyles[i].height;
                if (originalSvgStyles[i].viewBox) {
                    svg.setAttribute('viewBox', originalSvgStyles[i].viewBox);
                } else {
                    svg.removeAttribute('viewBox');
                }
            }
        });

        const imgData = canvas.toDataURL('image/png', 1.0); // Use highest quality PNG

        // Add new page if needed with proper positioning
        if (yOffset + dimensions.height > doc.internal.pageSize.getHeight() - this.pdfOptions.margins.bottom) {
            doc.addPage();
            yOffset = this.pdfOptions.margins.top;
        }

        // For bar charts, use the full dimensions height instead of calculating based on aspect ratio
        let finalHeight;
        if (isBarChart) {
            finalHeight = dimensions.height; // Use the full allocated height

            // Add a subtle background and border for the chart
            doc.setFillColor(255, 255, 255); // White background
            doc.setDrawColor(this.hexToRgb(this.pdfOptions.colors.border).r,
                this.hexToRgb(this.pdfOptions.colors.border).g,
                this.hexToRgb(this.pdfOptions.colors.border).b);
            doc.setLineWidth(0.5);

            // Draw rounded rectangle with shadow effect for chart container
            doc.roundedRect(
                dimensions.x - 5,
                yOffset - 5,
                dimensions.width + 10,
                finalHeight + 10,
                3,
                3,
                'FD'
            );

            // Add title for bar chart
            doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
            doc.setFontSize(this.pdfOptions.typography.baseSize + 1);
            doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
                this.hexToRgb(this.pdfOptions.colors.primary).g,
                this.hexToRgb(this.pdfOptions.colors.primary).b);
            doc.text('Time Distribution', dimensions.x, yOffset - 7);

            // Force the image to use the exact dimensions we want, ignoring aspect ratio
            doc.addImage(
                imgData,
                'PNG',
                dimensions.x,
                yOffset,
                dimensions.width,
                finalHeight,
                undefined,
                'NONE'  // Disable aspect ratio preservation
            );

            // Return early since we've already added the image
            return yOffset + finalHeight + 15; // Add more padding after chart
        } else {
            // Calculate aspect ratio for proper sizing of non-bar charts
            const aspectRatio = canvas.width / canvas.height;
            finalHeight = dimensions.width / aspectRatio;
        }

        // Add a subtle background and border for the chart
        doc.setFillColor(255, 255, 255); // White background
        doc.setDrawColor(this.hexToRgb(this.pdfOptions.colors.border).r,
            this.hexToRgb(this.pdfOptions.colors.border).g,
            this.hexToRgb(this.pdfOptions.colors.border).b);
        doc.setLineWidth(0.5);

        // Draw rounded rectangle with shadow effect for chart container
        doc.roundedRect(
            dimensions.x - 5,
            yOffset - 5,
            dimensions.width + 10,
            finalHeight + 10,
            3,
            3,
            'FD'
        );

        // Add title based on chart type
        const chartTitle = element.classList.contains('summary__donut-chart') ?
            'Project Distribution' :
            element.classList.contains('donut-chart') ?
                'Task Distribution' : 'Time Distribution';

        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setFontSize(this.pdfOptions.typography.baseSize + 1);
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);
        doc.text(chartTitle, dimensions.x, yOffset - 7);

        // Add the chart image with a slight offset for the container padding
        doc.addImage(imgData, 'PNG', dimensions.x, yOffset, dimensions.width, finalHeight);

        // Return the final y position but don't increment yOffset in the calling function
        // This allows the legend to be placed side by side with the chart
        return yOffset + finalHeight + 15; // Add more padding after chart
    }


    /**
     * Generates the PDF content with enhanced styling and structure
     */
    private async generatePdfContent(data: ReportData): Promise<jsPDF> {
        const doc = new jsPDF();
        this.pdfOptions.pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let yOffset = this.pdfOptions.margins.top;

        // Add professional header background
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);
        doc.rect(0, 0, this.pdfOptions.pageWidth, 22, 'F');

        // Add title
        doc.setFontSize(this.pdfOptions.typography.titleSize);
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setTextColor(255, 255, 255); // White text for header
        doc.text('Time Tracking Report', this.pdfOptions.pageWidth / 2, 15, { align: 'center' });

        yOffset = 35; // Position after header

        // Add decorative accent bar
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.accent).r,
            this.hexToRgb(this.pdfOptions.colors.accent).g,
            this.hexToRgb(this.pdfOptions.colors.accent).b);
        doc.rect(0, 22, this.pdfOptions.pageWidth, 2, 'F');

        // Reset font color for the rest of the document
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        // Add report metadata box
        const metaboxY = yOffset;
        const metaboxHeight = 35;

        // Add subtle background for the metadata section
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.background).r,
            this.hexToRgb(this.pdfOptions.colors.background).g,
            this.hexToRgb(this.pdfOptions.colors.background).b);
        doc.roundedRect(
            this.pdfOptions.margins.left - 5,
            metaboxY - 5,
            this.pdfOptions.pageWidth - this.pdfOptions.margins.left - this.pdfOptions.margins.right + 10,
            metaboxHeight,
            3,
            3,
            'F'
        );

        // Date range with improved formatting
        doc.setFontSize(this.pdfOptions.typography.subtitleSize);
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.text('Report Period:', this.pdfOptions.margins.left, metaboxY + 7);

        doc.setFont(this.pdfOptions.typography.bodyFont, 'normal');
        doc.setFontSize(this.pdfOptions.typography.baseSize);
        doc.text(
            `${data.dateRange.start.toLocaleDateString()} - ${data.dateRange.end.toLocaleDateString()}`,
            this.pdfOptions.margins.left + 50,
            metaboxY + 7
        );

        // Add generation timestamp
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setFontSize(this.pdfOptions.typography.subtitleSize);
        doc.text('Generated:', this.pdfOptions.margins.left, metaboxY + 17);

        doc.setFont(this.pdfOptions.typography.bodyFont, 'normal');
        doc.setFontSize(this.pdfOptions.typography.baseSize);
        doc.text(
            new Date().toLocaleString(),
            this.pdfOptions.margins.left + 50,
            metaboxY + 17
        );

        // Total hours with highlighted value
        const totalSeconds = data.timeTracks.reduce((acc, track) => acc + (track.time_spent || 0), 0);
        const totalHours = this.secondsToHours(totalSeconds);

        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setFontSize(this.pdfOptions.typography.subtitleSize);
        doc.text('Total Hours:', this.pdfOptions.margins.left, metaboxY + 27);

        // Use success color for the total hours value
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.success).r,
            this.hexToRgb(this.pdfOptions.colors.success).g,
            this.hexToRgb(this.pdfOptions.colors.success).b);
        doc.setFontSize(this.pdfOptions.typography.subtitleSize);
        doc.setFont(this.pdfOptions.typography.bodyFont, 'bold');
        doc.text(
            `${totalHours.toFixed(1)}h (${this.formatTimeHMS(totalSeconds)})`,
            this.pdfOptions.margins.left + 50,
            metaboxY + 27
        );

        // Reset text color
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        yOffset = metaboxY + metaboxHeight + 10;

        // Active filters
        yOffset = this.addFiltersSection(doc, data, yOffset);

        // Project and User summaries
        yOffset = await this.addSummariesSection(doc, data, yOffset);

        // Charts
        yOffset = await this.addChartsSection(doc, data, yOffset);

        // Client Details
        yOffset = this.addClientDetailsSection(doc, data, yOffset);

        // Add footer to all pages
        const pageCount = doc.internal.pages.length - 1; // -1 because jsPDF has a null page at index 0
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            // Footer line
            doc.setDrawColor(this.hexToRgb(this.pdfOptions.colors.border).r,
                this.hexToRgb(this.pdfOptions.colors.border).g,
                this.hexToRgb(this.pdfOptions.colors.border).b);
            doc.setLineWidth(0.5);
            doc.line(
                this.pdfOptions.margins.left,
                pageHeight - 15,
                this.pdfOptions.pageWidth - this.pdfOptions.margins.right,
                pageHeight - 15
            );

            // Page numbers
            doc.setFontSize(this.pdfOptions.typography.smallSize);
            doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.secondary).r,
                this.hexToRgb(this.pdfOptions.colors.secondary).g,
                this.hexToRgb(this.pdfOptions.colors.secondary).b);
            doc.text(
                `Page ${i} of ${pageCount}`,
                this.pdfOptions.pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );
        }

        return doc;
    }


    /**
     * Adds the filters section to the PDF
     */
    private addFiltersSection(doc: jsPDF, data: ReportData, yOffset: number): number {
        if (!Object.values(data.filters).some(arr => arr?.length > 0)) {
            return yOffset;
        }

        doc.text('Active Filters:', this.pdfOptions.margins.left, yOffset);
        yOffset += this.pdfOptions.spacing.paragraph;

        const addFilterLine = (label: string, names: string) => {
            doc.text(`- ${label}: ${names}`, this.pdfOptions.margins.left + 5, yOffset);
            yOffset += this.pdfOptions.spacing.paragraph;
        };

        if (data.filters.projects?.length) {
            const projectNames = data.projects
                .filter(p => data.filters.projects?.includes(p.id))
                .map(p => p.name)
                .join(', ');
            addFilterLine('Projects', projectNames);
        }

        if (data.filters.clients?.length) {
            const clientNames = data.projects
                .filter(p => p.client && data.filters.clients?.includes(p.client.id))
                .map(p => p.client?.name)
                .filter((name, i, arr) => name && arr.indexOf(name) === i)
                .join(', ');
            addFilterLine('Clients', clientNames);
        }

        if (data.filters.users?.length) {
            const userNames = data.users
                .filter(u => data.filters.users?.includes(u.id))
                .map(u => u.name)
                .join(', ');
            addFilterLine('Users', userNames);
        }

        return yOffset;
    }


    /**
     * Adds project and user summaries to the PDF
     */
    private async addSummariesSection(doc: jsPDF, data: ReportData, yOffset: number): Promise<number> {
        // Project Summary
        yOffset = this.addSectionHeader(doc, 'Project Summary', yOffset);
        const projectSummaries = this.getSummaryData(data);

        projectSummaries.forEach(({ name, hours, color }) => {
            const timeInSeconds = data.timeTracks
                .filter(track => track.project && track.project.name === name)
                .reduce((acc, track) => acc + (track.time_spent || 0), 0);

            yOffset = this.addColoredItem(
                doc,
                `${name}: ${hours.toFixed(1)}h (${this.formatTimeHMS(timeInSeconds)})`,
                color,
                this.pdfOptions.margins.left + 5,
                yOffset
            );
        });

        yOffset += this.pdfOptions.spacing.section;

        // User Summary
        yOffset = this.addSectionHeader(doc, 'User Summary', yOffset);
        const userSummaries = new Map<string, SummaryItem>();

        data.timeTracks.forEach(track => {
            if (track.author) {
                const current = userSummaries.get(track.author.name);
                userSummaries.set(track.author.name, {
                    name: track.author.name,
                    hours: (current?.hours || 0) + this.secondsToHours(track.time_spent || 0),
                    color: `#${Math.floor(parseInt(track.author.id.replace(/[^0-9]/g, ''), 10) % 16777215).toString(16).padStart(6, '0')}`
                });
            }
        });

        Array.from(userSummaries.values()).forEach(({ name, hours, color }) => {
            const timeInSeconds = data.timeTracks
                .filter(track => track.author && track.author.name === name)
                .reduce((acc, track) => acc + (track.time_spent || 0), 0);

            yOffset = this.addColoredItem(
                doc,
                `${name}: ${hours.toFixed(1)}h (${this.formatTimeHMS(timeInSeconds)})`,
                color,
                this.pdfOptions.margins.left + 5,
                yOffset
            );
        });

        return yOffset + this.pdfOptions.spacing.section;
    }

    /**
     * Adds a detailed client-wise breakdown section with enhanced layout and hierarchy
     */
    private addClientDetailsSection(doc: jsPDF, data: ReportData, yOffset: number): number {
        yOffset = this.addSectionHeader(doc, 'Client-wise Time Track Details', yOffset);

        // Group time tracks by client and project
        const clientTracks = new Map<string, {
            projectTracks: Map<string, {
                tasks: { title: string; hours: number }[];
                totalHours: number;
            }>;
            totalHours: number;
        }>();

        data.timeTracks.forEach(track => {
            if (!track.project?.client?.name) return;

            const clientName = track.project.client.name;
            const projectName = track.project.name;
            const hours = this.secondsToHours(track.time_spent || 0);

            // Initialize client if needed
            if (!clientTracks.has(clientName)) {
                clientTracks.set(clientName, {
                    projectTracks: new Map(),
                    totalHours: 0
                });
            }

            const client = clientTracks.get(clientName)!;

            // Initialize project if needed
            if (!client.projectTracks.has(projectName)) {
                client.projectTracks.set(projectName, {
                    tasks: [],
                    totalHours: 0
                });
            }

            // Add the track to the project
            const project = client.projectTracks.get(projectName)!;
            project.tasks.push({ title: track.title, hours });
            project.totalHours += hours;
            client.totalHours += hours;
        });

        // Skip if no client data
        if (clientTracks.size === 0) {
            return yOffset;
        }

        // Calculate grand total for percentages
        const grandTotal = Array.from(clientTracks.values())
            .reduce((acc, client) => acc + client.totalHours, 0);

        // Modern table styling
        const tableWidth = this.pdfOptions.pageWidth - this.pdfOptions.margins.left - this.pdfOptions.margins.right;
        const headers = ['Client / Project / Task', 'Hours', 'Percentage'];
        const colWidths = [tableWidth * 0.7, tableWidth * 0.15, tableWidth * 0.15]; // Proportional widths
        const rowHeight = 8; // Slightly taller rows for better readability
        const startX = this.pdfOptions.margins.left;

        // Draw table borders
        doc.setDrawColor(this.hexToRgb(this.pdfOptions.colors.border).r,
            this.hexToRgb(this.pdfOptions.colors.border).g,
            this.hexToRgb(this.pdfOptions.colors.border).b);
        doc.setLineWidth(0.1);

        // Header styling
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        // Draw rounded header with shadow effect
        doc.roundedRect(
            startX,
            yOffset,
            colWidths.reduce((a, b) => a + b, 0),
            rowHeight,
            2,
            2,
            'F'
        );

        // Header text in white for contrast
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setFontSize(this.pdfOptions.typography.baseSize);
        doc.setTextColor(255, 255, 255);

        // Draw header text with better alignment
        headers.forEach((header, i) => {
            const x = startX + (i > 0 ? colWidths.slice(0, i).reduce((a, b) => a + b, 0) : 0);
            doc.text(header, x + 4, yOffset + 5.5);
        });

        yOffset += rowHeight;

        // Reset text color for data rows
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        // Sort clients by total hours (descending)
        const sortedClientEntries = Array.from(clientTracks.entries())
            .sort((a, b) => b[1].totalHours - a[1].totalHours);

        // Process each client
        for (const [clientName, clientData] of sortedClientEntries) {
            // Check if we need a new page
            if (yOffset > doc.internal.pageSize.getHeight() - 40) {
                doc.addPage();
                yOffset = 20;

                // Redraw headers on new page
                doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
                    this.hexToRgb(this.pdfOptions.colors.primary).g,
                    this.hexToRgb(this.pdfOptions.colors.primary).b);
                doc.roundedRect(startX, yOffset, colWidths.reduce((a, b) => a + b, 0), rowHeight, 2, 2, 'F');

                doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
                doc.setTextColor(255, 255, 255);
                headers.forEach((header, i) => {
                    const x = startX + (i > 0 ? colWidths.slice(0, i).reduce((a, b) => a + b, 0) : 0);
                    doc.text(header, x + 4, yOffset + 5.5);
                });

                yOffset += rowHeight;
                doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
                    this.hexToRgb(this.pdfOptions.colors.primary).g,
                    this.hexToRgb(this.pdfOptions.colors.primary).b);
            }

            // Client row with special styling
            const clientPercentage = (clientData.totalHours / grandTotal * 100).toFixed(1);

            // Create a light background based on success color
            const clientBgColor = this.hexToRgb(this.pdfOptions.colors.success);
            const lightClientBgColor = {
                r: Math.min(clientBgColor.r + 180, 255),
                g: Math.min(clientBgColor.g + 180, 255),
                b: Math.min(clientBgColor.b + 180, 255)
            };

            // Client row with subtle rounded highlighting
            doc.setFillColor(lightClientBgColor.r, lightClientBgColor.g, lightClientBgColor.b);
            doc.roundedRect(
                startX,
                yOffset,
                colWidths.reduce((a, b) => a + b, 0),
                rowHeight,
                1,
                1,
                'F'
            );

            // Add thin border
            doc.setLineWidth(0.2);
            doc.roundedRect(
                startX,
                yOffset,
                colWidths.reduce((a, b) => a + b, 0),
                rowHeight,
                1,
                1,
                'S'
            );

            // Client name in bold
            doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
            doc.setFontSize(this.pdfOptions.typography.baseSize + 1);
            doc.text(clientName, startX + 4, yOffset + 5.5);

            // Client total hours
            const clientSeconds = data.timeTracks
                .filter(track => track.project?.client?.name === clientName)
                .reduce((acc, track) => acc + (track.time_spent || 0), 0);

            doc.text(
                `${clientData.totalHours.toFixed(1)} (${this.formatTimeHMS(clientSeconds)})`,
                startX + colWidths[0] + 4,
                yOffset + 5.5
            );

            // Client percentage
            doc.text(
                clientPercentage + '%',
                startX + colWidths[0] + colWidths[1] + 4,
                yOffset + 5.5
            );

            yOffset += rowHeight + 2; // Small extra spacing after client header

            // Sort projects by hours
            const sortedProjects = Array.from(clientData.projectTracks.entries())
                .sort((a, b) => b[1].totalHours - a[1].totalHours);

            // Process each project
            for (const [projectName, projectData] of sortedProjects) {
                // Check for page break
                if (yOffset > doc.internal.pageSize.getHeight() - 20) {
                    doc.addPage();
                    yOffset = 20;
                }

                const projectPercentage = (projectData.totalHours / grandTotal * 100).toFixed(1);

                // Project row with subtle background
                doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.background).r,
                    this.hexToRgb(this.pdfOptions.colors.background).g,
                    this.hexToRgb(this.pdfOptions.colors.background).b);
                doc.rect(startX, yOffset, colWidths.reduce((a, b) => a + b, 0), rowHeight, 'F');

                // Project name with arrow indicator
                doc.setFont(this.pdfOptions.typography.bodyFont, 'bold');
                doc.setFontSize(this.pdfOptions.typography.baseSize);
                doc.text(`→ ${projectName}`, startX + 8, yOffset + 5.5);

                // Project hours
                const projectSeconds = data.timeTracks
                    .filter(track => track.project?.name === projectName)
                    .reduce((acc, track) => acc + (track.time_spent || 0), 0);

                doc.text(
                    `${projectData.totalHours.toFixed(1)} (${this.formatTimeHMS(projectSeconds)})`,
                    startX + colWidths[0] + 4,
                    yOffset + 5.5
                );

                // Project percentage
                doc.text(
                    projectPercentage + '%',
                    startX + colWidths[0] + colWidths[1] + 4,
                    yOffset + 5.5
                );

                yOffset += rowHeight;

                // Sort tasks by hours
                const sortedTasks = projectData.tasks
                    .sort((a, b) => b.hours - a.hours);

                // Process each task
                sortedTasks.forEach((task, index) => {
                    if (yOffset > doc.internal.pageSize.getHeight() - 20) {
                        doc.addPage();
                        yOffset = 20;
                    }

                    const taskPercentage = (task.hours / grandTotal * 100).toFixed(1);

                    // Alternate row colors
                    const bgColor = this.hexToRgb(this.pdfOptions.colors.background);
                    const altBgColor = {
                        r: Math.max(bgColor.r - 10, 0),
                        g: Math.max(bgColor.g - 10, 0),
                        b: Math.max(bgColor.b - 10, 0)
                    };

                    doc.setFillColor(
                        index % 2 === 0 ? bgColor.r : altBgColor.r,
                        index % 2 === 0 ? bgColor.g : altBgColor.g,
                        index % 2 === 0 ? bgColor.b : altBgColor.b
                    );

                    doc.rect(startX, yOffset, colWidths.reduce((a, b) => a + b, 0), rowHeight, 'F');

                    // Task details with normal font
                    doc.setFont(this.pdfOptions.typography.bodyFont, 'normal');
                    doc.setFontSize(this.pdfOptions.typography.baseSize);

                    // Indent tasks and use bullet points
                    doc.text(`   • ${task.title}`, startX + 8, yOffset + 5.5);

                    // Task hours
                    const taskSeconds = task.hours * 3600; // Convert back to seconds for formatting

                    doc.text(
                        `${task.hours.toFixed(1)} (${this.formatTimeHMS(taskSeconds)})`,
                        startX + colWidths[0] + 4,
                        yOffset + 5.5
                    );

                    // Task percentage
                    doc.text(
                        taskPercentage + '%',
                        startX + colWidths[0] + colWidths[1] + 4,
                        yOffset + 5.5
                    );

                    yOffset += rowHeight;
                });
            }

            // Add spacing between clients
            yOffset += 5;
        }

        // Add grand total row with special styling
        if (yOffset > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            yOffset = 20;
        }

        // Total row with accent color
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);
        doc.roundedRect(
            startX,
            yOffset,
            colWidths.reduce((a, b) => a + b, 0),
            rowHeight,
            2,
            2,
            'F'
        );

        // Total text in white
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setFontSize(this.pdfOptions.typography.baseSize);
        doc.setTextColor(255, 255, 255);

        doc.text('GRAND TOTAL', startX + 4, yOffset + 5.5);
        const grandTotalSeconds = data.timeTracks
            .reduce((acc, track) => acc + (track.time_spent || 0), 0);

        doc.text(
            `${grandTotal.toFixed(1)} (${this.formatTimeHMS(grandTotalSeconds)})`,
            startX + colWidths[0] + 4,
            yOffset + 5.5
        );
        doc.text(
            '100.0%',
            startX + colWidths[0] + colWidths[1] + 4,
            yOffset + 5.5
        );

        // Reset text color
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);

        return yOffset + rowHeight + this.pdfOptions.spacing.section;
    }

    /**
     * Adds charts section to the PDF
     */
    private async addChartsSection(doc: jsPDF, data: ReportData, yOffset: number): Promise<number> {
        const graphElements = Array.from(
            document.querySelectorAll<HTMLElement>('.demo-chart, .summary__donut-chart, .donut-chart')
        );

        if (graphElements.length === 0) {
            return yOffset;
        }

        yOffset = this.addSectionHeader(doc, 'Time Distribution Charts', yOffset);
        //const initialYOffset = yOffset;

        for (const element of graphElements) {
            const isBarChart = element.classList.contains('demo-chart') &&
                !element.classList.contains('summary__donut-chart') &&
                !element.classList.contains('donut-chart');

            // For bar charts, use full width and add them separately
            if (isBarChart) {
                const dimensions = this.getChartDimensions(true);
                yOffset = await this.addChartImage(doc, element, dimensions, yOffset);
                continue;
            }

            // For donut charts, place chart and legend side by side
            const dimensions = this.getChartDimensions(false);

            // Add chart image without updating yOffset
            await this.addChartImage(doc, element, dimensions, yOffset);

            // Add legend next to the chart (side by side)
            const newYOffset = await this.addChartLegend(doc, element, data, dimensions, yOffset);

            // Update yOffset to the maximum height used by either chart or legend
            yOffset = newYOffset + this.pdfOptions.spacing.paragraph;

            // Check if we need a page break for the next chart
            if (yOffset > doc.internal.pageSize.getHeight() - 100) {
                doc.addPage();
                yOffset = this.pdfOptions.margins.top;
            }
        }

        return yOffset + this.pdfOptions.spacing.section;
    }


    /**
     * Gets chart dimensions based on type
     */
    private getChartDimensions(isBarChart: boolean): ChartDimensions {
        const pageWidth = this.pdfOptions.pageWidth;
        const margins = this.pdfOptions.margins;

        if (isBarChart) {
            const width = pageWidth - margins.left - margins.right;
            return {
                width,
                height: width * 0.7, // Increased ratio for better bar chart proportions
                x: margins.left,
                y: 0
            };
        }

        // For donut charts, use more width to make them larger
        const width = (pageWidth - margins.left - margins.right) * 0.5; // Increased width for donut charts
        return {
            width,
            height: width, // Height will be calculated based on aspect ratio
            x: margins.left,
            y: 0
        };
    }


    /**
     * Adds legend for donut charts
     */
    private async addChartLegend(
        doc: jsPDF,
        element: HTMLElement,
        data: ReportData,
        dimensions: ChartDimensions,
        yOffset: number
    ): Promise<number> {
        const pageWidth = this.pdfOptions.pageWidth;
        const margins = this.pdfOptions.margins;

        // Position legend to the right of the chart
        const legendX = dimensions.x + dimensions.width + 15;
        let legendY = yOffset;

        // Calculate aspect ratio for the chart height
        // Use a fixed aspect ratio instead of calculating from canvas to ensure consistency
        const aspectRatio = 1; // For donut charts, use 1:1 aspect ratio
        const chartHeight = dimensions.width / aspectRatio;

        // Add a subtle background for the legend
        doc.setFillColor(this.hexToRgb(this.pdfOptions.colors.background).r,
            this.hexToRgb(this.pdfOptions.colors.background).g,
            this.hexToRgb(this.pdfOptions.colors.background).b);

        // Get legend items based on chart type
        let legendItems: SummaryItem[] | DetailedSummaryItem[] = [];
        let legendTitle = '';

        if (element.classList.contains('summary__donut-chart')) {
            legendTitle = 'Summary by Category';
            legendItems = this.getSummaryData(data);
        } else if (element.classList.contains('donut-chart')) {
            legendTitle = 'Detailed Time Tracks';
            legendItems = this.getDetailedSummaryData(data);
        }

        // Calculate legend height to determine if we need a background
        const itemHeight = this.pdfOptions.spacing.paragraph;
        const estimatedLegendHeight = 10 + (legendItems.length * itemHeight);

        // Draw a rounded rectangle for the legend background
        doc.roundedRect(
            legendX - 5,
            legendY - 5,
            pageWidth - legendX - margins.right + 5,
            Math.max(chartHeight, estimatedLegendHeight) + 10,
            3,
            3,
            'F'
        );

        // Add legend title
        doc.setFontSize(12);
        doc.setFont(this.pdfOptions.typography.headerFont, 'bold');
        doc.setTextColor(this.hexToRgb(this.pdfOptions.colors.primary).r,
            this.hexToRgb(this.pdfOptions.colors.primary).g,
            this.hexToRgb(this.pdfOptions.colors.primary).b);
        doc.text(legendTitle, legendX, legendY);
        legendY += 10;

        // Add legend items with limited number to prevent overflow
        const maxLegendItems = 15; // Limit the number of legend items to display

        if (element.classList.contains('summary__donut-chart')) {
            const summaryData = this.getSummaryData(data)
                .slice(0, maxLegendItems); // Limit to top items

            summaryData.forEach(({ name, hours, color }) => {
                const timeInSeconds = data.timeTracks
                    .filter(track => track.project && track.project.name === name)
                    .reduce((acc, track) => acc + (track.time_spent || 0), 0);

                legendY = this.addColoredItem(
                    doc,
                    `${name}: ${hours.toFixed(1)}h (${this.formatTimeHMS(timeInSeconds)})`,
                    color,
                    legendX,
                    legendY
                );
            });

            // Add indication if items were truncated
            if (this.getSummaryData(data).length > maxLegendItems) {
                doc.setFont(this.pdfOptions.typography.bodyFont, 'italic');
                doc.setFontSize(this.pdfOptions.typography.smallSize);
                doc.text(
                    `+ ${this.getSummaryData(data).length - maxLegendItems} more items...`,
                    legendX,
                    legendY + 5
                );
                legendY += 10;
            }
        } else if (element.classList.contains('donut-chart')) {
            const detailedData = this.getDetailedSummaryData(data)
                .slice(0, maxLegendItems); // Limit to top items

            detailedData.forEach(({ name, title, hours, color }) => {
                const timeInSeconds = data.timeTracks
                    .filter(track =>
                        track.project &&
                        track.project.name === name &&
                        track.title === title
                    )
                    .reduce((acc, track) => acc + (track.time_spent || 0), 0);

                legendY = this.addColoredItem(
                    doc,
                    `${name} - ${title}: ${hours.toFixed(1)}h (${this.formatTimeHMS(timeInSeconds)})`,
                    color,
                    legendX,
                    legendY
                );
            });

            // Add indication if items were truncated
            if (this.getDetailedSummaryData(data).length > maxLegendItems) {
                doc.setFont(this.pdfOptions.typography.bodyFont, 'italic');
                doc.setFontSize(this.pdfOptions.typography.smallSize);
                doc.text(
                    `+ ${this.getDetailedSummaryData(data).length - maxLegendItems} more items...`,
                    legendX,
                    legendY + 5
                );
                legendY += 10;
            }
        }

        // Return the maximum y-position between chart and legend
        return Math.max(yOffset + chartHeight + 15, legendY);
    }


    /**
     * Exports the report data to a PDF file
     */
    public async exportToPdf(data: ReportData): Promise<void> {
        const doc = await this.generatePdfContent(data);
        doc.save('time-tracking-report.pdf');
    }

    /**
     * Exports the report data to a CSV file
     * Creates a CSV with detailed time tracking information including:
     * - Track title
     * - Project name
     * - Client name
     * - User name
     * - Hours spent
     * - Start date
     * - End date
     */
    public exportToCsv(data: ReportData): void {
        // CSV header
        const headers = [
            'Client',
            'Project',
            'User',
            'Tracking Name',
            'Duration (h)'
        ].join(',');

        // Transform time tracks to CSV rows
        const rows = data.timeTracks
            .filter(track => {
                // Filter by projects
                if (data.filters.projects?.length && track.project) {
                    if (!data.filters.projects.includes(track.project.id)) {
                        return false;
                    }
                }

                // Filter by clients
                if (data.filters.clients?.length && track.project?.client) {
                    if (!data.filters.clients.includes(track.project.client.id)) {
                        return false;
                    }
                }

                // Filter by tags
                if (data.filters.tags?.length) {
                    if (!track.tags.some(tag => data.filters.tags?.includes(tag.id))) {
                        return false;
                    }
                }

                // Filter by users
                if (data.filters.users?.length && track.author) {
                    if (!data.filters.users.includes(track.author.id)) {
                        return false;
                    }
                }

                return true;
            })
            .map(track => {
                const hours = this.secondsToHours(track.time_spent || 0);
                return [
                    `"${track.project?.client?.name || ''}"`,
                    `"${track.project?.name || ''}"`,
                    `"${track.author?.name || ''}"`,
                    `"${track.title.replace(/"/g, '""')}"`,
                    hours.toFixed(1)
                ].join(',');
            });

        // Combine headers and rows
        const csvContent = [headers, ...rows].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', 'time-tracking-report.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

}
